import * as React from "react";
import { Form, RadioProps, Radio, Grid } from "semantic-ui-react";
import { isEqual } from "lodash";

import { QuestionProps, AnsweredQuestion } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

export enum YesOrNo {
    Yes = "Yes",
    No = "No",
    DoNotKnow = "DoNotKnow",
    NotSure = "NotSure",
    NotApplicable = "Not Applicable"
}

interface RadioButtonProps {
    value: string;
    label: string;
    questionId?: string;
    answer?: string;
    onChange?: (event: React.FormEvent<HTMLInputElement>, props: RadioProps) => void;
}

const RadioButton: React.SFC<RadioButtonProps> = ({ value, label, questionId, answer, onChange }) => {
    return (
        <Form.Field key={value}>
            <Radio
                label={label}
                name={`question-${questionId}`}
                id={`question-${questionId}-${value}`}
                value={value}
                checked={value === answer}
                onChange={onChange}
            />
        </Form.Field>
    );
};

interface SpecificProps {
    horizontal?: boolean;
}

type RadioButtonQuestionProps = QuestionProps & SpecificProps;

class RadioButtonQuestion extends React.Component<RadioButtonQuestionProps> {
    constructor(props: RadioButtonQuestionProps) {
        super(props);

        this.onChange = this.onChange.bind(this);
    }

    public render() {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return null;
        }

        return (
            <QuestionWrapper
                {...this.props}
                question={this.props.questionConfig.questionText}
                error={this.props.showError && this.props.mandatory && !this.props.answer && "This field must be completed"}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
            >
                <Form.Field>
                    {this.renderValue()}
                </Form.Field>
            </QuestionWrapper>
        );
    }

    public componentDidUpdate(oldProps: RadioButtonQuestionProps) {
        if (!isEqual(oldProps.validationDependencies, this.props.validationDependencies)) {
            this.updateAnswer(this.props.answer as string);
        }
    }

    private renderValue() {
        if (this.props.readonly) {
            return (
                <div className={this.props.answerHighlightCriteria && this.props.answerHighlightCriteria(this.props.answer) ? "highlighted-answer" : ""}>
                    {React.Children
                        ?.map(this.props.children, (c: React.ReactElement) => c.props)
                        ?.find(c => c.value === this.props.answer)?.label ?? this.props.answer}
                </div>
            );
        }
        const { questionConfig, children } = this.props;
        const answer = this.props.answer as string;
        const onChange = this.onChange;
        const radioButtons = React.Children.map(children, (child) => {
            const radioButton = child as React.ReactElement<RadioButtonProps>;
            const newProps: RadioButtonProps = { ...radioButton.props, questionId: questionConfig.id, answer, onChange };
            return React.cloneElement(radioButton, newProps);
        });
        if (this.props.horizontal) {
            return (
                <Form>
                    <Grid>
                        <Grid.Column stretched verticalAlign="middle">
                            <Form.Group inline className="no-margin">
                                {radioButtons}
                            </Form.Group>
                        </Grid.Column>
                    </Grid>
                </Form>
            );
        } else {
            return radioButtons;
        }
    }

    private onChange(event: React.MouseEvent<HTMLInputElement>, props: RadioProps) {
        this.updateAnswer(props.value as string);
    }

    private updateAnswer = (value: string) => {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return;
        }
        const { questionConfig, updateQuestion } = this.props;
        const newQuestion: AnsweredQuestion = {
            question: questionConfig.questionText,
            answer: value,
            isValid: !this.props.mandatory || !!value
        };
        updateQuestion(newQuestion);
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }
    };
}

export { RadioButtonQuestion, RadioButton };
